import { IEstimateDiscountData } from "common/records/EstimateDiscount";
import { currentUser, currentUserOrgId } from "common/selectors/user.selectors";
import { TokenType } from "common/services/Auth.service";
import { store } from "common/store";
import { Nullable } from "common/types";
import ReactGA from "react-ga4";
import config from "react-global-configuration";
import UAParser from "ua-parser-js";

export interface TrackingData {
  category: string;
  component?: string;
  action: string;
  job: Nullable<number>;
  orgId: Nullable<number>;
  userId: Nullable<number>;
  user?: Nullable<number>;
  org?: Nullable<number>;
  estimate?: number;
  tool?: number | string;
  locked?: boolean;
  zoom?: number;
  hitType?: string;
  page?: string;
  userAgent?: UAParser.IResult;

  // Images
  image: string | number;
  images: number[];
  index?: number;

  // Documents
  file?: string;
  document?: number;
  import_type?: string;
  documents?: number[];

  // On Drag End
  oldIndex?: number;
  newIndex?: number;

  // Hover
  hoverJobId?: number;

  // Checkbox
  checkboxType?: string;

  // Input
  value?: any;

  // Products
  productId?: number;
  productOptionId?: number;

  // Estimate templates (apply-funnel events)
  estimateTemplate?: number;
  estimateGroupName?: string;

  // Events
  event?: number;
  startTime?: string;
  endTime?: string;

  navDisplay?: boolean;

  // Login
  email?: string;
  authType?: TokenType;

  eventType?: string;
  folder?: number;
  checkBoxType?: string;
  type?: string;
  name?: string;
  pattern?: number;
  isMulti?: boolean;
  presentation?: number;
  presentations?: number[];
  level?: string;

  //Accordion
  accordionName?: string;
  // Tasks
  count?: number;

  //Tools
  toolId?: number;

  // Discount
  discount?: IEstimateDiscountData;

  // Visualization
  visualization?: number;

  // Product Selector — disambiguates how a product was picked when "product added" fires
  source?: "search" | "tree";

  // Product option events — add/change/remove carry `product_option` = the ProductOption id. The two
  // quantity-changed events carry `product_option` = the EstimateLineItemOption id (rsf-app parity)
  // plus `product_option_id` = the ProductOption id; the "(not selected)" event has no ELIO yet, so it
  // omits `product_option`. `quantity` accompanies the quantity-changed events.
  product_option?: number;
  product_option_id?: number;
  quantity?: number;
}

if (config.get("GA_KEY")) {
  ReactGA.initialize(config.get("GA_KEY"), {
    testMode: process.env.NODE_ENV === "test" ? true : false,
  });
}

const localStorageKey = "rsf-analytics-queue";
const lastExceptionAction: Record<string, any> = {},
  counters: Record<string, any> = {},
  exceptionTimeout = 2000;

export const dispatch = (data: TrackingData): void => {
  if (config.get("GA_KEY")) {
    ReactGA.event(data);
  } else {
    // @ts-ignore
    (window.dataLayer = window.dataLayer || []).push(data);
  }
  dispatchToRsf(data);
};

export const dispatchException = (error: Error): void => {
  console.error(error);
  // @ts-ignore
  newrelic?.noticeError(error);
  const properties: any = {};

  if (window.location.search.indexOf("mobile") >= 0) {
    properties.mobile = true;
  }

  if (window.location.search.indexOf("embedded") >= 0) {
    properties.embedded = true;
  }

  const action = error.toString();
  if (Object.keys(lastExceptionAction).includes(action)) {
    if (counters[action] === undefined || counters[action] === null) {
      counters[action] = 0;
    }
    counters[action] += 1;
    if (lastExceptionAction[action] !== undefined && lastExceptionAction[action] !== null) {
      clearTimeout(lastExceptionAction[action]);
    }
    lastExceptionAction[action] = setTimeout(() => {
      delete lastExceptionAction[action];
      dispatchToRsf({
        hitType: "event",
        category: "Duplicate Exceptions",
        count: counters[action],
        action,
        label: error.stack,
        page: getPage(),
        isException: true,
        ...properties,
      });
      delete counters[action];
    }, exceptionTimeout);
    return;
  }
  lastExceptionAction[action] = setTimeout(() => {
    delete lastExceptionAction[action];
  }, exceptionTimeout);

  dispatchToRsf({
    hitType: "event",
    category: "Exceptions",
    action,
    label: error.stack,
    page: getPage(),
    isException: true,
    ...properties,
  });
};

const getPage = () => {
  return window.location.hash.substring(1) || window.location.pathname;
};

interface RsfAnalyticsData extends TrackingData {
  appModule?: string;
  hitType?: string;
  appVersion?: string;
}

export const dispatchToRsf = async (obj: RsfAnalyticsData): Promise<void> => {
  obj.appModule = config.get("ENVIRONMENT");
  obj.appVersion = config.get("VERSION");

  const actionUrl = `${config.get("RSF_ANALYTICS_URL")}/api/v1/events`;
  const code = config.get("RSF_ANALYTICS_KEY") || "test123 ";
  const body = { code, event: { data: obj } };

  try {
    const response = await fetch(actionUrl, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(body),
    });

    if (!response.ok) {
      // Don't save offline logs in development
      if (code !== "test123") {
        enqueueWithEventTime(obj);
      }
      return;
    }

    const chunk = dequeueAnalyticsChunk();
    if (!chunk.length) {
      return;
    }

    // Attempt to send any events generated without connectivity with
    // the analytics server.
    try {
      const response2 = await fetch(actionUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ code, events: chunk }),
      });

      if (!response2.ok) {
        enqueueAnalyticsData(chunk);
      }
    } catch (e) {
      enqueueAnalyticsData(chunk);
    }
  } catch (e) {
    // Don't save offline logs in development
    if (code !== "test123") {
      enqueueWithEventTime(obj);
    }
  }
};

/**
 * Call enqueueAnalyticsData with new Date()
 */
const enqueueWithEventTime = (obj: any | any[]) => {
  // Set the event time locally to preserve accuracy when posting
  // the events when connectivity has been restored.
  const eventTime = new Date().toISOString();
  enqueueAnalyticsData({ data: obj, inserted_at: eventTime });
};

/**
 * Adds element(s) to the RSF-Analytics queue and updates `localStorage`
 */
const enqueueAnalyticsData = (data: any | any[]) => {
  // Queue the record to be POSTED later
  const queue = getAnalyticsQueue();
  if (Array.isArray(data)) {
    queue.push(...data);
  } else {
    queue.push(data);
  }
  setAnalyticsQueue(queue);
};

/**
 * Dequeue a batch of records that matches `chunkSize` (or 5,000 when
 * omitted). The remaining items are returned to `localStorage`.
 */
const dequeueAnalyticsChunk = (chunkSize?: number) => {
  if (chunkSize === undefined) {
    chunkSize = 5_000;
  }

  const queue = getAnalyticsQueue();
  const chunk = queue.splice(0, chunkSize);
  setAnalyticsQueue(queue);

  return chunk;
};

/**
 * Returns an array representing RSF-Analytics data that exists in
 * `localStorage`
 */
const getAnalyticsQueue = (): any[] => {
  const queueJSON = localStorage.getItem(localStorageKey) || "[]";
  return JSON.parse(queueJSON);
};

/**
 * Overwrites the RSF-Analytics queue in `localStorage` with the value in
 * `queue`
 */
const setAnalyticsQueue = (queue: any[]) => {
  localStorage.setItem(localStorageKey, JSON.stringify(queue));
};

/**
 * Base tracking event helper
 * @param event
 */
export const trackEventHelper = (event: Partial<TrackingData>) => {
  const state = store.getState();
  const user = currentUser(state);
  const orgId = currentUserOrgId(state);

  const baseData = { userId: user?.id, orgId, page: location.pathname } as TrackingData;
  dispatch({ ...baseData, ...event });
};
